// apps/embed/src/entries/chat.tsx
//
// The SECOND embed entry (craco multi-entry → static/js/chat.js +
// static/css/chat.css). The TourTask plugin (and the generic <script> snippet)
// loads this site-wide and injects `<div id="tourtask-chat-root" ...>`; this
// mounts the floating chat launcher there. Separate from the booking entry
// (src/index.tsx) so a site that only wants chat never downloads the storefront.
//
// Browser-only: talks to /v1 with bizToken + Chat-Session-Token, never a server
// package. Mounts a LIGHT tree (no catalog fetch — chat doesn't need the
// storefront data), just the localized launcher.

// FIRST, before React and MUI evaluate: open the API connection (bootConnectionHints.ts).
import '@embed/entries/chatBootHints';
import { useContext, useEffect, useState } from 'react';
import GlobalStyles from '@mui/material/GlobalStyles';
import { ThemeProvider } from '@mui/material/styles';
import type { ResultError } from '@tourtask/types/result';
import { LocalizationProvider, type LocalizationInitial } from '@tourtask/storefront/contexts/LocalizationContext';
import LocalizationContext from '@tourtask/storefront/contexts/LocalizationContext';
import { fetchLocaleStrings } from '@tourtask/storefront/api/fetchLocaleStrings';
import { isWidgetSetupRefusal } from '@tourtask/storefront/api/widgetSetupRefusal';
import { CHAT_STRINGS_TO_FETCH } from '../chat-widget/chatStringsToFetch';
import { ChatWidget, chatThemeOf, type ChatBootProblem } from '../chat-widget/ChatWidget';
import { widgetScopedBaseline } from '@tourtask/storefront/components/themes/widgetScopedBaseline';
import { readChatWidgetLanguages, type ChatWidgetLanguages } from '../chat-widget/chatWidgetLanguage';
import { mountWidgetRoot } from '@tourtask/storefront/translation/mountWidgetRoot';
import { setStorefrontEnv } from '@tourtask/storefront/storefrontEnv';
import { EMBED_BUILD_ENV } from '@embed/entries/embedBuildEnv';

import '@embed/assets/reset-wp-themes.css';

// Before anything mounts: the build id every error report of this bundle carries.
setStorefrontEnv(EMBED_BUILD_ENV);

// The theme until the reference answers: MUI's own colours (the widget then takes the business's brand,
// `chatThemeOf` in ChatWidget), with the phone-country dropdown lifted above the panel.
const chatTheme = chatThemeOf(undefined);

// The widget's base styles on its own mount node and its popups, never on the partner page (apps/embed/AGENTS.md).
const chatBaseline = widgetScopedBaseline('tourtask-chat-root');

function ChatWidgetBootstrap({
  bizSlug,
  bizToken,
  language,
  defaultSide,
}: {
  bizSlug: string;
  bizToken: string;
  /** The host's `data-lang`, read (`readChatWidgetLanguages`): the chrome and conversation languages, or the code it names that TourTask does not support. */
  language: ChatWidgetLanguages;
  defaultSide: 'left' | 'right';
}) {
  const { setStrings } = useContext(LocalizationContext);
  // Why the strings fetch failed: the API refused the widget's setup (a wrong
  // business ID or token: the panel says so), or it could not be reached (local dev
  // with the API not running, or a TourTask outage: a connection-error notice).
  // Either replaces a UI full of `NO STRING IMPORTED…` placeholders.
  const [stringsError, setStringsError] = useState<ResultError | null>(null);
  // No strings exist in a language TourTask does not support, so none are fetched: the panel names the code.
  // The provider already starts in this language (`chatLocalization` below).
  const chromeLang = language.kind === 'ready' ? language.chromeLang : null;
  useEffect(() => {
    if (chromeLang === null) return;
    void fetchLocaleStrings({ bizSlug, bizToken, lang: chromeLang, keys: CHAT_STRINGS_TO_FETCH }).then((res) => {
      if (res.success) setStrings(res.data);
      else setStringsError(res.error);
    });
    // setStrings is a stable provider setter (a raw useState setter from
    // LocalizationProvider), so listing it never re-runs this.
  }, [bizSlug, bizToken, chromeLang, setStrings]);
  const bootProblem: ChatBootProblem | null =
    language.kind === 'unsupported'
      ? { kind: 'language', problem: language }
      : stringsError === null
        ? null
        : isWidgetSetupRefusal(stringsError)
          ? { kind: 'refused', error: stringsError }
          : { kind: 'connection' };
  return (
    <ChatWidget
      bizSlug={bizSlug}
      bizToken={bizToken}
      visitorLocale={language.kind === 'ready' ? language.visitorLocale : null}
      bootProblem={bootProblem}
      defaultSide={defaultSide}
    />
  );
}

const rootElement = document.getElementById('tourtask-chat-root');

// No-op when the root is absent — this entry co-exists with the booking entry in
// the dev index.html, where there is no #tourtask-chat-root.
if (rootElement) {
  // `data-business-id` is canonical; `data-business-slug` is the legacy fallback
  // (mirrors src/index.tsx). The value (numeric id or slug) flows into the /v1
  // path; the server's resolveBizIdOrSlug accepts either.
  const bizSlug =
    rootElement.getAttribute('data-business-id') ||
    rootElement.getAttribute('data-business-slug') ||
    '';
  const bizToken = rootElement.getAttribute('data-business-token') || '';
  // The chrome loads in the host's language, else English; the conversation carries the host's
  // language only when it names one; a code TourTask does not support is named in the panel
  // (chatWidgetLanguage.ts).
  const language = readChatWidgetLanguages(rootElement.getAttribute('data-lang'));
  // The provider starts in the chrome language, so ChatWidget's first render, and its one `/chat/reference` fetch,
  // already have it. Started empty, the reference was fetched in English and again once the bootstrap had set the
  // language (docs/embed-size-and-speed.md §6.1 #6). The strings follow from ChatWidgetBootstrap. Nothing for a
  // language TourTask does not support, which loads nothing.
  const chatLocalization: LocalizationInitial | undefined =
    language.kind === 'ready' ? { langCode: language.chromeLang, strings: {} } : undefined;
  // Which bottom corner the launcher starts in. The WordPress plugin sets this
  // from its "Chat position" setting via `data-chat-position="left"`; anything
  // else (including the attribute being absent) keeps the conventional right
  // corner.
  const defaultSide: 'left' | 'right' = rootElement.getAttribute('data-chat-position') === 'left' ? 'left' : 'right';

  if (bizSlug && bizToken) {
    // fallback 'none': a crashed floating launcher must not paint an error box
    // over the partner's page.
    mountWidgetRoot(
      rootElement,
      <ThemeProvider theme={chatTheme}>
        <GlobalStyles styles={chatBaseline} />
        <LocalizationProvider initial={chatLocalization}>
          <ChatWidgetBootstrap bizSlug={bizSlug} bizToken={bizToken} language={language} defaultSide={defaultSide} />
        </LocalizationProvider>
      </ThemeProvider>,
      { widget: 'chat', fallback: 'none', telemetry: { bizId: bizSlug, bizToken } },
    );
  } else {
    console.warn('[tourtask-chat] #tourtask-chat-root is missing data-business-id or data-business-token');
  }
}
